import { getTelegramPlatform } from '../telegram';

/**
 * Що вміє платформа — єдине місце, де плеєр про це питає. Вигляд у плеєра
 * скрізь однаковий; різниться лише те, які кнопки мають сенс і як саме
 * розгортається повний екран.
 */

/** Розширення WebKit, яких немає в типах DOM. */
export interface WebKitVideo extends HTMLVideoElement {
  webkitShowPlaybackTargetPicker?: () => void;
  webkitCurrentPlaybackTargetIsWireless?: boolean;
  webkitEnterFullscreen?: () => void;
  webkitSupportsPresentationMode?: (mode: string) => boolean;
  webkitSetPresentationMode?: (mode: string) => void;
  webkitPresentationMode?: string;
}

/**
 * - `ios-video` — iPhone та iPad (і в Telegram, і в браузері): системний
 *   повноекранний плеєр iOS. Він сам повертає екран і звичний власникам iPhone.
 * - `telegram` — решта клієнтів Telegram (Android, десктоп, веб): браузерний
 *   Fullscreen API у їхніх WebView не працює, тож розгортаємо сам застосунок
 *   і кладемо плеєр шаром на все його вікно.
 * - `native` — звичайний Fullscreen API (браузер на комп'ютері й Android).
 * - `overlay` — нічого з переліченого: шар на всю сторінку.
 */
export type FullscreenMode = 'telegram' | 'native' | 'ios-video' | 'overlay';

/** Apple WebKit (iPhone, iPad, Safari): сам грає HLS, і лише так AirPlay передає картинку. */
export function isAppleWebKit(v: HTMLVideoElement): boolean {
  return 'webkitShowPlaybackTargetPicker' in v && v.canPlayType('application/vnd.apple.mpegurl') !== '';
}

/** Клієнт Telegram, у якому відкрито Mini App, або '' поза Telegram. SDK
 *  підключений завжди й у звичайному браузері називає платформу `unknown`. */
function telegramClient(): string {
  const platform = getTelegramPlatform();
  return platform === 'unknown' ? '' : platform;
}

/** У Telegram на Android розгорнутий плеєр ще й кладемо на бік: екран там не
 *  розвернути інакше (lockOrientation лише фіксує поточну орієнтацію). */
export function rotatesInFullscreen(): boolean {
  return telegramClient() === 'android';
}

export function fullscreenMode(v: HTMLVideoElement): FullscreenMode {
  const tg = telegramClient();
  const iosVideo = typeof (v as WebKitVideo).webkitEnterFullscreen === 'function';
  if (tg === 'ios') return iosVideo ? 'ios-video' : 'telegram';
  if (tg) return 'telegram';
  if (document.fullscreenEnabled) return 'native';
  // Без Fullscreen API лишається iPhone у Safari; у WKWebView на Mac метод теж
  // є, але нічого не розгортає — тому дивимось на сенсорний екран.
  if (iosVideo && navigator.maxTouchPoints > 0) return 'ios-video';
  return 'overlay';
}

export function supportsAirPlay(v: HTMLVideoElement): boolean {
  return typeof (v as WebKitVideo).webkitShowPlaybackTargetPicker === 'function';
}

export function supportsPip(v: HTMLVideoElement): boolean {
  if (document.pictureInPictureEnabled && typeof v.requestPictureInPicture === 'function') return true;
  return (v as WebKitVideo).webkitSupportsPresentationMode?.('picture-in-picture') ?? false;
}

/** На телефонах гучність — це кнопки пристрою; iOS взагалі не дає міняти її з коду. */
export function canSetVolume(): boolean {
  return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
}
